import React, { useEffect, useRef, useState, useContext, useMemo } from 'react';
import { ThemeContext } from '../App';

const AnalogClock = ({ size = 16, className = '', onClick }) => {
  const { 
    mode, 
    weatherCondition, 
    weatherThemeEnabled, 
    weatherThemeMode, 
    selectedWeatherTheme 
  } = useContext(ThemeContext);
  const isDarkMode = mode === 'dark';
  
  // Get the effective weather condition based on theme mode
  const effectiveWeatherCondition = weatherThemeEnabled
    ? (weatherThemeMode === 'manual' ? selectedWeatherTheme : weatherCondition)
    : 'clear'; // Force a default condition for testing
    
  console.log('Clock - Weather condition:', effectiveWeatherCondition);
  console.log('Clock - Weather theme enabled:', weatherThemeEnabled);
  
  const clockRef = useRef(null);
  const hourHandRef = useRef(null);
  const minuteHandRef = useRef(null);
  // Use state to force re-renders and tracking
  const [currentTime, setCurrentTime] = useState(new Date());

  // Function to calculate rotation angles based on current time
  const updateClockHands = () => {
    // Get the current time directly from the system
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    
    // Convert to 12-hour format for the clock rotation
    let hour12 = hours % 12;
    if (hour12 === 0) hour12 = 12;
    
    // Calculate angles:
    // Hour hand: each hour represents 30 degrees (360/12), 
    // and each minute adds a fraction of that (30/60 = 0.5 degrees per minute)
    // Subtract 90 degrees to start from 12 o'clock position
    const hourAngle = (hour12 * 30) + (minutes * 0.5) - 90;
    
    // Minute hand: each minute represents 6 degrees (360/60)
    // Subtract 90 degrees to start from 12 o'clock position
    const minuteAngle = minutes * 6 - 90;
    
    // Apply rotations with consistent transforms
    if (hourHandRef.current) {
      hourHandRef.current.style.transform = `rotate(${hourAngle}deg)`;
    }
    
    if (minuteHandRef.current) {
      minuteHandRef.current.style.transform = `rotate(${minuteAngle}deg)`;
    }
    
    // Update current time in state
    setCurrentTime(now);
  };
  
  useEffect(() => {
    // Set initial positions immediately when component mounts
    updateClockHands();
    
    // Update frequently for more accurate time
    const interval = setInterval(() => {
      updateClockHands();
    }, 10000); // Every 10 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Generate hour markers based on size
  const renderHourMarkers = () => {
    if (size < 100) return null;
    
    return Array.from({ length: 12 }).map((_, i) => {
      const angle = i * 30;
      const radian = (angle - 90) * (Math.PI / 180); // Start from 12 o'clock
      const isMainMark = i % 3 === 0;
      
      // For main marks (12, 3, 6, 9) - position the numbers
      if (isMainMark) {
        // Position numbers slightly inward from the edge
        const numberDistance = 38; // Numbers are a bit closer to center
        const numX = 50 + numberDistance * Math.cos(radian);
        const numY = 50 + numberDistance * Math.sin(radian);
        
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              fontSize: `${size / 15}px`,
              fontWeight: i === 0 ? 'bold' : '500',
              top: `${numY}%`,
              left: `${numX}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 0,
              color: 'currentColor'
            }}
          >
            {i === 0 ? 12 : i === 3 ? 3 : i === 6 ? 6 : 9}
          </div>
        );
      }
      // For minor marks between the numbers
      else {
        // Calculate positions for minor dashes - closer to center
        const dashOuterDistance = 43; // Outer point of dash (closer to edge)
        const dashInnerDistance = 38; // Inner point of dash (closer to center)
        
        const outerX = 50 + dashOuterDistance * Math.cos(radian);
        const outerY = 50 + dashOuterDistance * Math.sin(radian);
        const innerX = 50 + dashInnerDistance * Math.cos(radian);
        const innerY = 50 + dashInnerDistance * Math.sin(radian);
        
        // Calculate dash thickness (width) and length
        const dashWidth = 1;
        const dashLength = Math.sqrt(
          Math.pow(outerX - innerX, 2) + 
          Math.pow(outerY - innerY, 2)
        ) * size / 100;
        
        // Calculate rotation angle for the dash (perpendicular to radius)
        const dashRotation = angle + 90; // Rotate 90 degrees from radius direction
        
        return (
          <div 
            key={i}
            style={{
              position: 'absolute',
              width: `${dashWidth}px`,
              height: `${dashLength}px`,
              backgroundColor: 'currentColor',
              top: `${outerY}%`,
              left: `${outerX}%`,
              transform: `translate(-50%, -50%) rotate(${angle}deg)`,
              transformOrigin: 'center',
              zIndex: 0
            }}
          />
        );
      }
    });
  };

  // Create array of 60 minute markers
  const renderMinuteMarkers = () => {
    if (size < 150) return null;
    
    return Array.from({ length: 60 }).map((_, i) => {
      // Skip positions where hour markers are
      if (i % 5 === 0) return null;
      
      const angle = i * 6; // 6 degrees per minute
      const radian = (angle - 90) * (Math.PI / 180);
      const markDistance = 46; // Very close to the edge
      const markX = 50 + markDistance * Math.cos(radian);
      const markY = 50 + markDistance * Math.sin(radian);
      
      return (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: `${size / 120}px`,
            height: `${size / 120}px`,
            backgroundColor: 'currentColor',
            borderRadius: '50%',
            top: `${markY}%`,
            left: `${markX}%`,
            transform: 'translate(-50%, -50%)',
            opacity: 0.4,
            zIndex: 0
          }}
        />
      );
    });
  };

  // renderHourMarkers()/renderMinuteMarkers() (up to 72 positioned divs,
  // each computed with trig calls) depend only on the `size` prop, which
  // never changes for a mounted clock - but they were called fresh in JSX
  // on every render, including the state-only re-render the 10s interval
  // above forces just to keep the `title` tooltip current. Memoize them
  // against `size` so a tick doesn't rebuild every marker.
  const hourMarkers = useMemo(() => renderHourMarkers(), [size]);
  const minuteMarkers = useMemo(() => renderMinuteMarkers(), [size]);

  // Get accent color based on weather condition
  const getAccentColor = (condition, isDark) => {
    condition = condition.toLowerCase();
    
    switch(condition) {
      case 'clear':
        return isDark ? '#ffa000' : '#ff9800'; // Amber for clear/sunny
      case 'clouds':
        return isDark ? '#78909c' : '#607d8b'; // Blue-gray for clouds
      case 'rain':
      case 'drizzle':
        return isDark ? '#26c6da' : '#00acc1'; // Cyan/teal for rain
      case 'snow':
        return isDark ? '#90caf9' : '#42a5f5'; // Blue for snow
      case 'thunderstorm':
        return isDark ? '#9575cd' : '#5e35b1'; // Purple for thunderstorm
      default:
        return isDark ? '#bb86fc' : '#1976d2';
    }
  };

  // Set background color based on theme and weather
  const defaultBgColor = isDarkMode ? '#1e1e1e' : 'white';
  const defaultTextColor = isDarkMode ? '#e0e0e0' : '#212529';
  
  // Weather-based colors
  const weatherTextColor = (weatherThemeEnabled && effectiveWeatherCondition) 
    ? (isDarkMode ? '#ffffff' : '#212529')
    : defaultTextColor;
    
  const accentColor = (weatherThemeEnabled && effectiveWeatherCondition) 
    ? getAccentColor(effectiveWeatherCondition, isDarkMode)
    : (isDarkMode ? '#bb86fc' : '#1976d2');

  // Weather-based background color
  const getWeatherClockFace = (condition, isDark) => {
    if (!condition) return defaultBgColor;
    condition = condition.toLowerCase();
    
    switch(condition) {
      case 'clear':
        return isDark ? '#ff9100' : '#ffe082';  // Amber for clear/sunny
      case 'clouds':
        return isDark ? '#263238' : '#cfd8dc';  // Gray for cloudy
      case 'rain':
      case 'drizzle':
        return isDark ? '#00695c' : '#e0f7fa';  // Teal/cyan for rainy
      case 'snow':
        return isDark ? '#546e7a' : '#e1f5fe';  // Pale blue for snowy
      case 'thunderstorm':
        return isDark ? '#4a148c' : '#d1c4e9';  // Purple for stormy
      default:
        return defaultBgColor;
    }
  };
  
  const bgColor = getWeatherClockFace(effectiveWeatherCondition, isDarkMode);
    
  console.log('Clock - Background color:', bgColor);
    
  const textColor = weatherTextColor;
  const handColor = (weatherThemeEnabled && effectiveWeatherCondition) ? accentColor : textColor;

  return (
    <div 
      ref={clockRef}
      className={`analog-clock ${className}`} 
      style={{ 
        position: 'relative',
        width: `${size}px`, 
        height: `${size}px`,
        borderRadius: '50%',
        border: `1px solid ${textColor}`,
        display: 'inline-block',
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: bgColor,
        color: textColor,
        boxShadow: weatherThemeEnabled && effectiveWeatherCondition 
          ? `0 0 15px ${accentColor}66, inset 0 0 10px rgba(0, 0, 0, 0.2)` 
          : (size >= 100 ? 'inset 0 0 10px rgba(0, 0, 0, 0.1)' : 'none'),
        transition: 'background-color 0.5s ease, color 0.3s ease, box-shadow 0.3s ease'
      }}
      onClick={onClick}
      title={currentTime.toLocaleTimeString()}
    >
      {/* Clock face */}
      <div className="clock-face" style={{ width: '100%', height: '100%', position: 'relative' }}>
        {/* Minute markers */}
        {minuteMarkers}

        {/* Hour markers */}
        {hourMarkers}
        
        {/* Hour hand */}
        <div 
          ref={hourHandRef}
          className="hour-hand"
          style={{
            position: 'absolute',
            width: '35%',
            height: size >= 100 ? '4px' : '2px',
            backgroundColor: handColor,
            top: '50%',
            left: '50%',
            transformOrigin: '0 50%',
            transform: 'rotate(0deg)',
            zIndex: 1,
            transition: 'background-color 0.5s ease'
          }}
        />
        
        {/* Minute hand */}
        <div 
          ref={minuteHandRef}
          className="minute-hand"
          style={{
            position: 'absolute',
            width: '45%',
            height: size >= 100 ? '2px' : '1px',
            backgroundColor: handColor,
            top: '50%',
            left: '50%',
            transformOrigin: '0 50%',
            transform: 'rotate(0deg)',
            zIndex: 2,
            transition: 'background-color 0.5s ease'
          }}
        />
        
        {/* Center dot */}
        <div 
          className="center-dot"
          style={{
            position: 'absolute',
            width: size >= 100 ? '6px' : '3px',
            height: size >= 100 ? '6px' : '3px',
            backgroundColor: weatherThemeEnabled && effectiveWeatherCondition ? accentColor : textColor,
            borderRadius: '50%',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            transition: 'background-color 0.5s ease'
          }}
        />
      </div>
    </div>
  );
};

export default AnalogClock; 